import util from '../../lib/util';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from "../find";
import Task from '../../entities/task';
import Invoice from '../../entities/invoice';
import Document from '../../entities/document';
import Comment from '../../entities/comment';

/*
 * Delete/nullify anywhere this task is used in other entities
 *
 * @param {Task} task
 */
export function deleteTask(task: Task, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = task.id;
    }
    /* Simple delete usages */
        
    store.getState().invoices
        .getMappingFor('taskId', task.id)
        .filter(invoiceTemp => invoiceTemp.status !== 'D')
        .forEach(invoiceTemp => {
            const _invoice = invoiceTemp.clone();
            _invoice.status =  'U';
            _invoice.addedOn = newHRTimestamp();
            _invoice.importSource = targetImportSource;
            _invoice.importId = mergeId;

            _invoice.taskIds = _invoice.taskIds?.filter(id => id !== task.id) || [];
            store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
        });

    store.getState().invoices
        .getMappingFor('taskId', task.id)
        .filter(invoiceTemp => invoiceTemp.status !== 'D')
        .forEach(invoiceTemp => {
            const _invoice = invoiceTemp.clone();
            _invoice.status =  'U';
            _invoice.addedOn = newHRTimestamp();
            _invoice.importSource = targetImportSource;
            _invoice.importId = mergeId;

            _invoice.taskPositions = _invoice.taskPositions?.filter(id => id !== task.id) || [];
            store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
        });

    /* Remove the actual task */
    task.status = 'D';
    task.addedOn = newHRTimestamp();
    task.addedBy = getCurrentUserId();
    task.importSource = targetImportSource;
    task.importId = mergeId;
    store.dispatch(Actions.TaskActions.removeTaskQueue(task));
    

    /* Delete any documents */
    findAll<Document>('documents')
         .filter(documentTemp => documentTemp.entityType === 'task' && documentTemp.entityId === task.id && documentTemp.status !== 'D')
         .forEach(documentTemp => {
            const _document = documentTemp.clone();
            // Delete the target
            _document.status = 'D';
            _document.addedOn = newHRTimestamp();
            _document.addedBy = getCurrentUserId();
            _document.importSource = targetImportSource;
            _document.importId = mergeId;
            store.dispatch(Actions.DocumentActions.removeDocumentQueue(_document));
         });
    

    /* Delete any comments */
    findAll<Comment>('comments')
         .filter(commentTemp => commentTemp.entityType === 'task' && commentTemp.entityId === task.id && commentTemp.status !== 'D')
         .forEach(commentTemp => {
            const _comment = commentTemp.clone();
            // Delete the target
            _comment.status = 'D';
            _comment.addedOn = newHRTimestamp();
            _comment.addedBy = getCurrentUserId();
            _comment.importSource = targetImportSource;
            _comment.importId = mergeId;
            store.dispatch(Actions.CommentActions.removeCommentQueue(_comment));
         });
    
}

/*
 * Replace anywhere this task is used in other entities.
 *
 * @param {Task} task
 * @param {Task} replaceWith
 * @return {Task} - Task will have refs stripped away
 */
export function replaceTask(task: Task, replaceWith: Task, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = task.id;
    }
    /* Simple replace usages */
        
    store.getState().invoices
        .getMappingFor('taskId', task.id)
        .filter(invoiceTemp => invoiceTemp.status !== 'D')
        .forEach(invoiceTemp => {
            const _invoice = invoiceTemp.clone();
            _invoice.status =  'U';
            _invoice.addedOn = newHRTimestamp();
            _invoice.importSource = targetImportSource;
            _invoice.importId = mergeId;

            _invoice.taskIds = _invoice.taskIds?.map(id => (id === task.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
        });

    store.getState().invoices
        .getMappingFor('taskId', task.id)
        .filter(invoiceTemp => invoiceTemp.status !== 'D')
        .forEach(invoiceTemp => {
            const _invoice = invoiceTemp.clone();
            _invoice.status =  'U';
            _invoice.addedOn = newHRTimestamp();
            _invoice.importSource = targetImportSource;
            _invoice.importId = mergeId;

            _invoice.taskPositions = _invoice.taskPositions?.map(id => (id === task.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
        });

    /* Move any documents */
    findAll<Document>('documents')
         .filter(documentTemp => documentTemp.entityType === 'task' && documentTemp.entityId === task.id && documentTemp.status !== 'D')
         .forEach(documentTemp => {
            const _document = documentTemp.clone();
            // Replace the target with the new ID
			_document.entityId = replaceWith.id;
            _document.status = 'U';
            _document.addedOn = newHRTimestamp();
            _document.addedBy = getCurrentUserId();
            _document.importSource = targetImportSource;
            _document.importId = mergeId;
            store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
         });
    

    /* Move any comments */
    findAll<Comment>('comments')
         .filter(commentTemp => commentTemp.entityType === 'task' && commentTemp.entityId === task.id && commentTemp.status !== 'D')
         .forEach(commentTemp => {
            const _comment = commentTemp.clone();
            // Replace the target with the new ID
			_comment.entityId = replaceWith.id;
            _comment.status = 'U';
            _comment.addedOn = newHRTimestamp();
            _comment.addedBy = getCurrentUserId();
            _comment.importSource = targetImportSource;
            _comment.importId = mergeId;
            store.dispatch(Actions.CommentActions.saveCommentQueue(_comment));
         });
    
    return task;
}

/*
 * Determine the impact of deleting this task from other entities
 *
 * @param {Task} task
 * @return {Array.<{count: number, entity: string}>} - a list of impacted entities, along with a count of impacted entities
 */
export function deleteImpactTask(task: Task) {
    const impacts: string[] = [];
    const state = store.getState();
    
    /* Simple delete usages */
        
    const invoiceTaskIdsImpact = state.invoices
          .getMappingFor('taskId', task.id)
          .filter(invoice => invoice.status !== 'D');

    invoiceTaskIdsImpact.forEach(() => impacts.push('invoices'));

    const invoiceTaskPositionsImpact = state.invoices
          .getMappingFor('taskId', task.id)
          .filter(invoice => invoice.status !== 'D');

    invoiceTaskPositionsImpact.forEach(() => impacts.push('invoices'));

    return util.countDistinct(impacts);
}